import { useState } from 'react';
import * as Sentry from '@sentry/react';

export type ContactPayload = {
  name: string;
  email: string;
  kind: string;
  budget: string;
  message: string;
};

export type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit';
// Web3Forms access keys are public by design (client-side submission).
const ACCESS_KEY = import.meta.env.VITE_WEB3FORMS_KEY || '3aa4fc58-314d-4360-9710-aec42c515681';

/* Submits the contact form via Web3Forms. The enquirer's email is set as
   reply-to so a reply lands straight back to them. */
export const useSubmitContact = () => {
  const [status, setStatus] = useState<SubmitStatus>('idle');

  const submit = async (data: ContactPayload, captchaToken: string) => {
    setStatus('submitting');
    try {
      // FormData (not JSON) keeps this a CORS-safe request with no preflight.
      const formData = new FormData();
      formData.append('access_key', ACCESS_KEY);
      formData.append('subject', `Project enquiry — ${data.kind}`);
      formData.append('from_name', 'HVDM Website');
      formData.append('replyto', data.email);
      formData.append('name', data.name);
      formData.append('email', data.email);
      formData.append('kind', data.kind);
      formData.append('budget', data.budget);
      formData.append('message', data.message);
      // hCaptcha token — Web3Forms verifies this server-side when hCaptcha is
      // enabled for the form in the dashboard.
      formData.append('h-captcha-response', captchaToken);

      const res = await fetch(WEB3FORMS_ENDPOINT, {
        method: 'POST',
        body: formData
      });

      const result = await res.json();
      if (!res.ok || !result.success) {
        throw new Error(result.message || `Web3Forms responded ${res.status}`);
      }

      setStatus('success');
    } catch (error) {
      Sentry.captureException(error);
      setStatus('error');
    }
  };

  const reset = () => setStatus('idle');

  return { status, submit, reset };
};
